<template>
    <div>
        <Listbox
            v-slot="{ open }"
            v-model="inputValue"
            as="div"
            :autocomplete="autoComplete"
            class="flex flex-col"
            :disabled="disabled"
            @focus="onFocus()"
            @blur="onBlur()"
            @change="onChange()"
        >
            <ListboxLabel
                v-if="label"
                :class="[labelClasses, customLabelClasses, 'text-gray-800']"
            >
                {{ label }}
                <span v-if="required"> * </span>
            </ListboxLabel>
            <div
                class="relative rounded-md focus-within:text-gray-900 text-gray-500"
            >
                <ListboxButton
                    :data-test="`open_select_${name}`"
                    :class="[
                        disabled ? 'bg-gray-100' : 'bg-white',
                        setup ? 'h-14 p-4' : 'py-2 px-3',
                        inputClasses,
                    ]"
                >
                    <div
                        v-if="selectedValue"
                        class="flex items-center w-full gap-3"
                    >
                        <component
                            :is="selectedValue.prependIcon"
                            v-if="selectedValue.prependIcon"
                            :class="[
                                prependIconClasses,
                                selectedValue.prependIconClass,
                            ]"
                        />
                        <span
                            class="block truncate text-gray-800"
                            :class="customTextClass"
                            :style="{ maxWidth: selectedValueMaxWidth }"
                            >{{ selectedValue.text }}</span
                        >
                    </div>
                    <div v-else class="italic" :class="customPlaceholderClass">
                        {{
                            placeholder ||
                            $t("generic.lms_selectbox_placeholder")
                        }}
                    </div>
                    <ChevronDownIcon
                        class="h-2 text-gray-800 transition-transform flex-shrink-0"
                        :class="{
                            'rotate-180 transform':
                                (open && !top) || (top && !open),
                            'w-3': !setup,
                            'w-4': setup,
                        }"
                        aria-hidden="true"
                    />
                </ListboxButton>
                <transition
                    leave-active-class="transition ease-in duration-100"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <ListboxOptions
                        :class="top ? 'bottom-0.5 mb-11' : 'mt-1'"
                        class="absolute z-30 w-full shadow-lg bg-white rounded-2xl py-2 md:pb-4 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none scrollbar-styled flex flex-col gap-4 md:gap-2"
                        :style="{ maxHeight: dropDownMaxHeight + 'px' }"
                    >
                        <ListboxOption
                            v-for="option in options"
                            :key="option.value"
                            v-slot="{ active, selected }"
                            as="template"
                            :value="option.value"
                            :data-test="option.value"
                        >
                            <li
                                :class="[
                                    {
                                        'text-gray-840': active || selected,
                                        'bg-gray-100': active,
                                        'text-gray-800': !active && !selected,
                                    },
                                    'cursor-default select-none px-4 py-3 flex justify-between items-center',
                                ]"
                            >
                                <div class="flex items-center gap-2">
                                    <component
                                        :is="option.prependIcon"
                                        v-if="option.prependIcon"
                                        class="h-4 shrink-0"
                                        :class="
                                            active
                                                ? option.prependIconActiveClass
                                                : option.prependIconClass
                                        "
                                    />
                                    <span
                                        :class="[
                                            selected
                                                ? 'font-semibold'
                                                : 'font-normal',
                                            'block text-left',
                                        ]"
                                    >
                                        {{ option.text }}
                                    </span>
                                </div>

                                <CheckIcon
                                    v-if="markSelected"
                                    class="border-0.375 border-gray-840 inline-block rounded-full fill-current flex-shrink-0 text-gray-840 h-5 w-5 p-1"
                                    :class="{ invisible: !selected }"
                                />
                            </li>
                        </ListboxOption>
                    </ListboxOptions>
                </transition>
            </div>
            <div v-if="error || info" class="text-subtitle-2 ml-2 h-5">
                <span v-if="error" class="text-alert">{{ error }}</span>
                <span v-if="info && !error" class="text-gray-700">
                    {{ info }}
                </span>
            </div>
        </Listbox>
    </div>
</template>

<script setup>
import CheckIcon from "@/Components/Icons/CheckIcon.vue";
import ChevronDownIcon from "@/Components/Icons/ChevronDownIcon.vue";
import { fieldProps, useFieldSetup } from "@/Mixins/FormFieldMixin";
import {
    Listbox,
    ListboxButton,
    ListboxLabel,
    ListboxOption,
    ListboxOptions,
} from "@headlessui/vue";
import { twMerge } from "tailwind-merge";
import { computed, useSlots, onMounted, onUnmounted, ref } from "vue";

const props = defineProps({
    ...fieldProps,
    options: {
        type: Array,
        default: () => [],
    },
    top: {
        type: Boolean,
        default: false,
    },
    setup: {
        type: Boolean,
        default: false,
    },
    customLabelClass: {
        type: String,
        default: "text-label",
    },
    customTextClass: {
        type: String,
        default: "",
    },
    customPlaceholderClass: {
        type: String,
        default: "text-gray-500",
    },
    markSelected: {
        type: Boolean,
        default: true,
    },
    prependIconClasses: {
        type: String,
        default: "h-4 w-4",
    },
    customInputClass: {
        type: String,
        default: "",
    },
});

const slots = useSlots();
const emit = defineEmits(["update:modelValue", "focus", "blur"]);
const { inputValue, onChange, onFocus, onBlur } = useFieldSetup(props, emit);

const labelClasses = computed(() => {
    return [{ "text-label-sm": props.sm }, { "text-label-lg": props.lg }];
});

const customLabelClasses = computed(() =>
    twMerge("mb-2 font-semibold", props.customLabelClass)
);

const inputClasses = computed(() => {
    return [
        twMerge(
            "rounded-lg border border-gray-500 placeholder:italic focus:border-gray-900 focus:ring-1 focus:ring-gray-900 active:outline-none text-body text-gray-900 w-full text-left cursor-default focus:outline-none flex items-center justify-between",
            props.customInputClass
        ),
        { "border-alert ring-1 ring-alert": Boolean(props.error) },
        { "py-1 text-subtitle-1": props.sm },
        { "py-3 text-lead-1": props.lg },
        { "pl-9": slots.prepend },
        { "pr-9": slots.append },
    ];
});

const selectedValue = computed(() =>
    props.options.find((option) => option.value === inputValue.value)
);

onMounted(() => {
    resizeModalHandler();
    setSelectedValueMaxWidth();
    window.addEventListener("resize", resizeModalHandler);
    window.addEventListener("scroll", resizeModalHandler);
});

onUnmounted(() => {
    window.removeEventListener("resize", resizeModalHandler);
    window.removeEventListener("scroll", resizeModalHandler);
});

const dropDownMaxHeight = ref("240px");
const selectedValueMaxWidth = ref("100%");

const resizeModalHandler = () => {
    const screenHeight = window.innerHeight;
    let selectFieldYPosition = null;
    let element = null;

    if (
        (element = document.querySelector(
            `[data-test="open_select_${props.name}"]`
        ))
    ) {
        selectFieldYPosition = props.top
            ? element.getBoundingClientRect().top
            : element.getBoundingClientRect().bottom;
    }

    if (!selectFieldYPosition) {
        return;
    }

    if (props.top) {
        dropDownMaxHeight.value = selectFieldYPosition - 16;

        return;
    }

    dropDownMaxHeight.value = screenHeight - selectFieldYPosition - 16;
};

const setSelectedValueMaxWidth = () => {
    const element = document.querySelector(
        `[data-test="open_select_${props.name}"]`
    );

    if (!element) {
        return;
    }

    const elementWidth = element.getBoundingClientRect().width;

    if (elementWidth < 130) {
        return;
    }

    selectedValueMaxWidth.value =
        element.getBoundingClientRect().width - 64 + "px";
};
</script>
